import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, LogOut, TrendingUp, AlertTriangle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import CampanaNotificaciones from './CampanaNotificaciones';
import BuscadorGeneral from './BuscadorGeneral';
import SelectorEmpresa from './SelectorEmpresa';
import { obtenerIniciales, formatMonto, formatFecha } from '../utils/format';

function fechaLocalHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function esAyer(fecha, hoyStr) {
  const [y, m, d] = hoyStr.split('-').map(Number);
  const ayer = new Date(y, m - 1, d - 1);
  const ayerStr = `${ayer.getFullYear()}-${String(ayer.getMonth() + 1).padStart(2, '0')}-${String(ayer.getDate()).padStart(2, '0')}`;
  return fecha === ayerStr;
}

function haceMinutos(iso) {
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  return min < 1 ? 'recién' : min < 60 ? `hace ${min} min` : `hace ${Math.floor(min / 60)} h ${min % 60} min`;
}

// Tarjeta con las tasas del día (BCV oficial y USD mercado) de
// tasas_cambio_dia. Si la de hoy no está cargada muestra la última
// anterior, marcada en ámbar con su fecha; la USD genérica puede ir un día
// atrás de la BCV (carga automática, #136) y entonces se marca aparte.
// Suma el promedio de Binance P2P (#135), solo visual. Se refresca al
// cambiar de pantalla (así toma la tasa recién cargada en /tasas-cambio),
// cada 10 min y al volver a la pestaña; Binance solo con la pestaña visible
// (el servidor la renueva a lo sumo cada hora).
function TarjetaTasasHoy() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tasas, setTasas] = useState(undefined);
  const [binance, setBinance] = useState(null);

  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      if (document.hidden) return;
      api
        .get('/tasas-cambio/ultima', { params: { hasta: fechaLocalHoy() } })
        .then((res) => vivo && setTasas(res.data))
        .catch(() => vivo && setTasas(null));
      api
        .get('/tasas-cambio/binance')
        .then((res) => vivo && setBinance(res.data))
        .catch(() => vivo && setBinance(null));
    };
    cargar();
    const id = setInterval(cargar, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', cargar);
    window.addEventListener('tasas-cambio:actualizado', cargar);
    return () => {
      vivo = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', cargar);
      window.removeEventListener('tasas-cambio:actualizado', cargar);
    };
  }, [location.pathname]);

  if (tasas === undefined) return null;

  if (!tasas) {
    return (
      <button
        onClick={() => navigate('/tasas-cambio')}
        title="Cargar las tasas de hoy"
        className="hidden sm:flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
      >
        <AlertTriangle size={14} />
        Tasas de hoy sin cargar
      </button>
    );
  }

  const hoyStr = fechaLocalHoy();
  const esDeHoy = tasas.fecha === hoyStr;
  const etiqueta = (fecha) => (esAyer(fecha, hoyStr) ? 'Ayer' : formatFecha(fecha).slice(0, 5));
  const etiquetaFecha = esDeHoy ? null : etiqueta(tasas.fecha);
  const usdAtrasada = tasas.fecha_usd && tasas.fecha_usd !== tasas.fecha;

  return (
    <button
      onClick={() => navigate('/tasas-cambio')}
      title={esDeHoy ? 'Tasas de cambio de hoy' : `Tasa del ${formatFecha(tasas.fecha)} — falta cargar la de hoy`}
      className="group hidden sm:flex items-stretch overflow-hidden rounded-xl border border-turquoise-100 bg-turquoise-50 text-left transition hover:bg-turquoise-100/60 hover:border-turquoise-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-500/60"
    >
      {esDeHoy ? (
        <span className="flex items-center px-2.5 text-turquoise-600 transition group-hover:text-turquoise-700">
          <TrendingUp size={16} />
        </span>
      ) : (
        <span className="flex flex-col items-center justify-center gap-0.5 px-2.5 text-amber-600">
          <Clock size={13} />
          <span className="text-[9px] font-semibold uppercase leading-none tracking-wide">{etiquetaFecha}</span>
        </span>
      )}
      <span className="flex flex-col justify-center border-l border-turquoise-100 px-3 py-1">
        <span className="text-[10px] font-semibold uppercase leading-none tracking-wider text-turquoise-600">BCV</span>
        <span className="mt-0.5 text-sm font-semibold leading-tight tabular-nums text-turquoise-700">
          {formatMonto(tasas.tasa_bcv)}
        </span>
      </span>
      <span
        className="flex flex-col justify-center border-l border-turquoise-100 px-3 py-1"
        title={usdAtrasada ? `USD del ${formatFecha(tasas.fecha_usd)} — falta cargar la USD de hoy` : undefined}
      >
        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase leading-none tracking-wider text-green-600">
          USD
          {usdAtrasada && <span className="normal-case tracking-normal text-amber-600">· {etiqueta(tasas.fecha_usd)}</span>}
        </span>
        <span className={`mt-0.5 text-sm font-semibold leading-tight tabular-nums ${usdAtrasada ? 'text-amber-700' : 'text-turquoise-700'}`}>
          {tasas.tasa_usd_generica != null ? formatMonto(tasas.tasa_usd_generica) : '—'}
        </span>
      </span>
      {binance && (
        <span
          className="flex flex-col justify-center border-l border-turquoise-100 px-3 py-1"
          title={`Binance P2P USDT/VES · promedio de ${binance.anuncios} anuncios para comprar USDT, sin el 10 % más alto ni el más bajo · rango ${formatMonto(binance.minimo)} – ${formatMonto(binance.maximo)} · ${haceMinutos(binance.actualizado_en)}${binance.desactualizada ? ' · Binance no responde, último valor obtenido' : ''}`}
        >
          <span className="text-[10px] font-semibold uppercase leading-none tracking-wider text-yellow-700">P2P</span>
          <span className={`mt-0.5 text-sm font-semibold leading-tight tabular-nums ${binance.desactualizada ? 'text-amber-700' : 'text-turquoise-700'}`}>
            {formatMonto(binance.promedio)}
          </span>
        </span>
      )}
    </button>
  );
}

export default function Topbar({ onAbrirMenu }) {
  const { usuario, logout, rolActual, esAdminGlobal } = useAuth();

  return (
    <header className="h-16 flex-shrink-0 bg-white border-b border-slate-200 flex items-center justify-between gap-2 px-3 sm:px-6 print:hidden">
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onAbrirMenu}
          className="flex-shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 md:hidden"
        >
          <Menu size={20} />
        </button>
        <SelectorEmpresa />
        <BuscadorGeneral />
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <TarjetaTasasHoy />

        <CampanaNotificaciones />

        {usuario?.foto_url ? (
          <img
            src={usuario.foto_url}
            alt=""
            referrerPolicy="no-referrer"
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-navy-900 text-xs font-semibold text-turquoise-300">
            {obtenerIniciales(usuario?.nombre)}
          </div>
        )}
        {/* Nombre en la primera línea y cargo (rol) en la segunda. */}
        <div className="hidden sm:block min-w-0 max-w-[180px] leading-tight">
          <p className="truncate text-sm font-medium text-slate-700">{usuario?.nombre}</p>
          <p className="truncate text-xs capitalize text-turquoise-700">
            {esAdminGlobal ? 'Administrador global' : rolActual}
          </p>
        </div>

        <button
          onClick={logout}
          title="Cerrar sesión"
          className="flex-shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50"
        >
          <LogOut size={18} />
          <span className="sr-only">Salir</span>
        </button>
      </div>
    </header>
  );
}
